/**
 * The first thing on a parent's dashboard: where each of their children is right now.
 *
 * This is the block the portal was missing. A parent opening it at three in the afternoon is
 * asking one of two questions — "is my kid checked in" or "when is their class" — and until
 * now the answer to the first lived only on the Students page, one tap and one child at a
 * time, while the answer to the second was nowhere at all.
 *
 * The card is careful about which of those two it is answering. A session with a record is a
 * fact and gets a coloured pill; a child with nothing recorded gets their next scheduled class
 * in grey, with no claim about attendance attached. Mixing those up is how the portal
 * previously told Flex families they had a session on a day they had not bought.
 */

import { Link } from 'react-router-dom';
import { DoorOpen } from 'lucide-react';
import { useAttendanceByStudents } from '../../hooks/useAttendance';
import { LocationPill } from '../LocationPill';
import { useFlexPlanDates } from '../../hooks/useFlexPlanDates';
import { ParentAttendancePill, ParentAttendanceTimes } from '../attendance/ParentAttendancePill';
import { initialsFor, solidTintFor } from '../StudentAvatar';
import { TYPE_CLOCK_WIDE, TYPE_OBJECT_WIDE, TYPE_SECTION_WIDE, TYPE_SUBJECT_WIDE } from '../typeScale';
import { useLabelParts, useWeekdayDateLabel } from '../../config/useLabels';
import { getTodaySessionDate } from '../../utils/schedule';
import {
  buildTodaySessions,
  getNextSession,
  getTodayScheduledSessions,
  type TodaySession,
  type NextSessionEnrollment,
} from '../../utils/todaySessions';

export interface TodayStudent {
  id: string;
  /**
   * Full name. Drives the initials and the colour, both of which must match the Students
   * page, and is what the row is headed with: two siblings who share a first name are not
   * hypothetical in a school, and the card names people rather than addressing them.
   */
  name: string;
  enrollments: NextSessionEnrollment[];
}

/**
 * One line on a child's day.
 *
 * Two kinds, because the card makes two kinds of statement and the whole design rests on not
 * blurring them: `recorded` is a fact a teacher entered and may wear a coloured pill;
 * `schedule` is what a timetable says and never may — whether it is today's timetable or, on
 * an empty day, the one line the card allows itself about another day.
 */
type DayItem =
  | { kind: 'recorded'; key: string; startMinutes: number; session: TodaySession }
  | {
      /** A timetable line. `when` is already phrased — "Later today", "Scheduled", "Fri, Aug 28". */
      kind: 'schedule';
      key: string;
      startMinutes: number;
      programName: string;
      locationName: string | null;
      timeRange: string | null;
      when: string;
    };

/** What one child has to say today, in the order the day runs. */
interface StudentDay {
  student: TodayStudent;
  items: DayItem[];
  /** Whether any of it is about today, which is what the card is allowed to be named for. */
  hasToday: boolean;
}

export function TodaySection({ students, className = '' }: { students: TodayStudent[]; className?: string }) {
  const label = useLabelParts();
  const weekdayDate = useWeekdayDateLabel();
  const today = getTodaySessionDate();
  const query = useAttendanceByStudents(students.map(s => s.id), today);
  // `?? []` rather than a destructuring default: the query is disabled when a parent has no
  // students yet, and a disabled query's `data` is null, not undefined. Getting this wrong
  // throws during render, which on the top block of the page blanks the whole dashboard.
  const records = query.data ?? [];
  // Only once a child is actually there to collect: the same rule the check-out function
  // applies — an after-school session, checked in, today — so the button never opens a screen
  // with nothing on it.
  const canCheckOut = records.some(r => r.status === 'checked_in' && r.programType === 'after-school');

  // Flex is the one billing type whose days cannot be read off a schedule. The lookup is
  // empty — and the query never runs — for a family with no Flex enrollment.
  const flexEnrollmentIds = students.flatMap(s =>
    s.enrollments.filter(e => e.programs?.billing_type === 'flex_monthly').map(e => e.id),
  );
  const flexDates = useFlexPlanDates(flexEnrollmentIds, today);

  const days: StudentDay[] = students.map(student => {
    const sessions = buildTodaySessions(student.enrollments, records.filter(r => r.studentId === student.id));

    // A Flex enrollment carries its confirmed days; every other kind ignores the field.
    const enrollments = student.enrollments.map(e =>
      flexDates[e.id] ? { ...e, selectedDays: flexDates[e.id] } : e,
    );

    // Every class today's timetable holds, minus the ones a teacher has already recorded
    // something against — those are on screen as facts, and a schedule line beneath a pill
    // would be the same class described twice.
    const recorded = new Set(sessions.map(s => s.enrollmentId));
    const scheduled = getTodayScheduledSessions(enrollments, today)
      .filter(item => !recorded.has(item.enrollmentId));

    // Interleaved, not concatenated. A morning class nobody recorded belongs above an
    // afternoon check-in, because that is the order the child lived it.
    const items: DayItem[] = [
      ...sessions.map((session): DayItem => ({
        kind: 'recorded',
        key: `rec-${session.enrollmentId}`,
        startMinutes: session.startMinutes,
        session,
      })),
      ...scheduled.map((item): DayItem => ({
        kind: 'schedule',
        key: `sch-${item.enrollmentId}`,
        startMinutes: item.startMinutes,
        programName: item.programName,
        locationName: item.locationName,
        timeRange: item.timeRange,
        // Announced while it is ahead; merely stated once it has begun. "Scheduled" says what
        // the timetable holds and claims nothing at all about where the child is.
        when: item.hasStarted ? 'Scheduled' : 'Later today',
      })),
    ].sort((a, b) => a.startMinutes - b.startMinutes);

    if (items.length > 0) return { student, items, hasToday: true };

    // Only with the day genuinely empty — a weekend, a closure, a gap between terms — does
    // the card look further out, and then to exactly one line.
    const next = getNextSession(enrollments, today);
    return {
      student,
      items: next
        ? [{
            kind: 'schedule',
            key: 'next',
            startMinutes: Infinity,
            programName: next.programName,
            locationName: next.locationName,
            timeRange: next.timeRange,
            when: weekdayDate(next.date),
          }]
        : [],
      hasToday: false,
    };
  });

  const shown = days.filter(d => d.items.length > 0);
  if (shown.length === 0) return null;

  // The card is named for what it actually contains. On a Saturday, a closure, or in a gap
  // between terms it holds only a line about some future day, and calling that "Today" would
  // be a small lie in the heading of the page.
  //
  // What it is *not* keyed on is whether a teacher has tapped anything. That was the first
  // rule here and it was wrong: a family whose after-school starts at noon opened the portal
  // at nine to a card headed "Next class" listing a class happening that same day. The
  // heading names the day; the lines under it are what stay careful about the difference
  // between a record and a timetable.
  const anyToday = shown.some(d => d.hasToday);
  const heading = label(anyToday ? 'header.today' : 'header.nextClass');

  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 ${className}`}
      aria-label={heading.en}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className={TYPE_SECTION_WIDE}>
          {heading.zh && <span className="mr-1.5 font-normal normal-case tracking-normal">{heading.zh}</span>}
          {heading.en}
        </h2>
        {anyToday && (
          <span className={TYPE_SECTION_WIDE}>{weekdayDate(today, { weekday: 'long' })}</span>
        )}
      </div>

      {/* One child per row, at every width. Side by side they left the card short and half
          empty, which unbalanced it against the taller column beside it. */}
      <div className="divide-y divide-gray-100">
        {shown.map(({ student, items }) => (
          <div
            key={student.id}
            className="grid grid-cols-[30px_1fr] items-start gap-x-3 py-3 first:pt-0 last:pb-0 lg:grid-cols-[40px_1fr] lg:gap-x-4 lg:py-4"
          >
            <span
              className={`flex h-[30px] w-[30px] items-center justify-center rounded-full text-[11px] font-bold text-white lg:h-10 lg:w-10 lg:text-[13px] ${solidTintFor(student.name)}`}
              aria-hidden="true"
            >
              {initialsFor(student.name)}
            </span>

            <div className="min-w-0">
              <h3 className={TYPE_SUBJECT_WIDE}>{student.name}</h3>

              {/* One fact per line, and never two things sharing one.

                  The row used to put the scheduled hours and the state pill side by side to
                  the right of the class name. That held while there were two of them; the
                  arrival and collection times — which `parentPickupDetails` restores — made
                  three competing things on a single line, a lot to take in for a card whose
                  whole job is to be read at a glance.

                  So the left column stacks — class, the hours it is meant to run, then the
                  clock readings if there are any — and only the pill stays on the class's own
                  line, at `lg`, where the column is wide enough to carry both. On a phone it
                  drops beneath them, which is where it has always sat. */}
              {items.map((item, index) => (
                <div
                  key={item.key}
                  className={`py-2 last:pb-0 ${index === 0 ? 'pt-1.5' : 'border-t border-gray-100'} ${
                    item.kind === 'recorded' ? 'lg:flex lg:items-baseline lg:justify-between lg:gap-4' : ''
                  }`}
                >
                  {item.kind === 'recorded' ? (
                    <>
                      <div className="min-w-0 lg:flex-1">
                        <p className={TYPE_OBJECT_WIDE}>{item.session.programName}</p>
                        {item.session.timeRange && (
                          <p className={`${TYPE_CLOCK_WIDE} mt-0.5 whitespace-nowrap`}>{item.session.timeRange}</p>
                        )}
                        <LocationPill name={item.session.locationName} className="mt-1" />
                        <ParentAttendanceTimes
                          record={item.session.record}
                          mode={item.session.mode}
                          className="mt-0.5 block lg:text-[13px]"
                        />
                      </div>
                      <div className="mt-1.5 lg:mt-0 lg:flex-none">
                        <ParentAttendancePill record={item.session.record} mode={item.session.mode} />
                      </div>
                    </>
                  ) : (
                    /* A timetable line: grey, no border, no fill, nothing that could be read
                       as a state. The wording was decided where the data was, so there is one
                       shape to render however the line was arrived at. */
                    <>
                      <p className={TYPE_OBJECT_WIDE}>{item.programName}</p>
                      <p className={`${TYPE_CLOCK_WIDE} mt-0.5`}>
                        {item.when}
                        {item.timeRange ? ` · ${item.timeRange}` : ''}
                      </p>
                      <LocationPill name={item.locationName} className="mt-1" />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {canCheckOut && (
        <Link
          to="/pickup"
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-semibold text-white hover:bg-blue-700 lg:w-auto lg:px-6"
        >
          <DoorOpen className="h-5 w-5" aria-hidden="true" />
          {label('action.checkOutPickup').zh && (
            <span className="font-normal">{label('action.checkOutPickup').zh}</span>
          )}
          {label('action.checkOutPickup').en}
        </Link>
      )}
    </section>
  );
}
